import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';
import { getSearchOrigin } from '../../../components/ListingCard/ListingCard.helpers';

import css from './SearchResultsPanel.module.css';

// How many skeleton cards to show while a fresh search loads. Fills ~2 rows on the 3-col grid,
// reading as a deliberate "loading" state rather than a blank/dimmed panel.
const SKELETON_CARD_COUNT = 6;

// Branded placeholder for a single listing card while results load: a 4/3 image block (matching the
// real card's aspect ratio) + two text bars, all with a subtle shimmer. Purely decorative — the host
// <li> is aria-hidden so AT announces only the aria-busy "Loading jobs" region, not 6 blank list items.
const SkeletonCard = () => (
  <div className={css.skeletonCard}>
    <div className={css.skeletonImage} />
    <div className={css.skeletonLineWide} />
    <div className={css.skeletonLineMedium} />
    <div className={css.skeletonLineNarrow} />
    <div className={css.skeletonLineNarrow} />
  </div>
);

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @param {boolean} [props.loading] - When true, render branded skeleton cards instead of results
 * @param {string[]} [props.haulerEquipment] - the viewing hauler's kit; when given, each card shows
 *   whether they have everything the job needs. Null for customers.
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    loading = false,
    haulerEquipment = null,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const gridClass = isMapVariant ? css.listingCardsMapVariant : css.listingCards;

  // Haul.Taxi: where the user searched, so each card can show "~N mi away". Null for a
  // keyword-only search (no location in the URL) — the cards then just omit the distance.
  const searchOrigin = getSearchOrigin(search);

  // Fresh-search loading state: a grid of branded skeleton cards at full opacity (the caller skips
  // the stale-results dim in this case), so the page reads as "loading" rather than blank.
  if (loading) {
    return (
      <div className={classes}>
        <ul
          className={gridClass}
          aria-busy="true"
          aria-label={
            intl ? intl.formatMessage({ id: 'SearchResultsPanel.loadingResults' }) : undefined
          }
        >
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
            <li key={`skeleton-${i}`} className={css.resultItem} aria-hidden="true">
              <SkeletonCard />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Haul.Taxi: the grid is capped at 3 columns (1 col <550px, 2 cols up to
      // --viewportLarge 1024px, 3 cols beyond), so advertise ~1/3-viewport widths
      // instead of the old 4/5-column sizes (which under-fetched on wide screens).
      return ['(max-width: 549px) 100vw', '(max-width: 1023px) 50vw', '33vw'].join(', ');
    }
  };

  return (
    <div className={classes}>
      <ul className={gridClass}>
        {listings.map(l => (
          <li key={l.id.uuid} className={css.resultItem}>
            <ListingCard
              className={css.listingCard}
              listing={l}
              renderSizes={cardRenderSizes(isMapVariant)}
              setActiveListing={setActiveListing}
              searchOrigin={searchOrigin}
              haulerEquipment={haulerEquipment}
            />
          </li>
        ))}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
